import {
  SAMPLING_LANE_MAX_LENGTH,
  SAMPLING_LANE_PATTERN,
  SAMPLING_REGIONS,
  SAMPLING_SCOPES,
  SAMPLING_STREAMS,
  samplingPolicyKey,
} from '@nadoutong/contracts';
import type {
  SamplingPolicyInput,
  SamplingPolicyRecord,
  SamplingRegion,
  SamplingScope,
  SamplingStream,
} from '@nadoutong/contracts';

/**
 * Pure helpers behind the sampling-policy editor (#2862, #3199). The
 * validation mirrors the `sampling_policies` CHECK constraints (migrations
 * 0103, 0128) so a draft the console accepts is one the table accepts:
 *   - ratios in [0, 1]; thresholds are integers ≥ 1;
 *   - `region` scope carries no id, `org` only org, `sku` only SKU,
 *     `org_sku` both, `lane` only a lane, `org_lane` org + lane; a lane is a
 *     SKU namespace and a draft may pick several (one row per lane);
 *   - `size_threshold_bytes` / `large_full_keep_ratio` are trace-only;
 *   - one row per (stream, region, scope, org, SKU).
 * Every value is nullable: empty = inherit from the broader layer.
 */

export const SAMPLING_VALUE_FIELDS = [
  'keep_ratio',
  'keep_slow_ms',
  'size_threshold_bytes',
  'large_full_keep_ratio',
] as const;
export type SamplingValueField = (typeof SAMPLING_VALUE_FIELDS)[number];

const TRACE_ONLY_FIELDS: ReadonlySet<SamplingValueField> = new Set([
  'size_threshold_bytes',
  'large_full_keep_ratio',
]);

export function isTraceOnlyField(field: SamplingValueField): boolean {
  return TRACE_ONLY_FIELDS.has(field);
}

export function samplingFieldKind(field: SamplingValueField): 'ratio' | 'int' {
  return field === 'keep_ratio' || field === 'large_full_keep_ratio' ? 'ratio' : 'int';
}

export function fieldsForStream(stream: SamplingStream): readonly SamplingValueField[] {
  return SAMPLING_VALUE_FIELDS.filter((field) => stream === 'trace' || !isTraceOnlyField(field));
}

/** Broad → specific: the order operators read a policy set in. */
export const SCOPE_DISPLAY_ORDER: readonly SamplingScope[] = [...SAMPLING_SCOPES].reverse();

export function scopeNeedsOrg(scope: SamplingScope): boolean {
  return scope === 'org' || scope === 'org_sku' || scope === 'org_lane';
}

export function scopeNeedsLane(scope: SamplingScope): boolean {
  return scope === 'lane' || scope === 'org_lane';
}

export function scopeNeedsSku(scope: SamplingScope): boolean {
  return scope === 'sku' || scope === 'org_sku';
}

export interface SamplingPolicyGroup {
  readonly stream: SamplingStream;
  readonly region: SamplingRegion;
}

export const SAMPLING_POLICY_GROUPS: readonly SamplingPolicyGroup[] = SAMPLING_STREAMS.flatMap(
  (stream) => SAMPLING_REGIONS.map((region) => ({ stream, region })),
);

export function toPolicyInput(record: SamplingPolicyRecord): SamplingPolicyInput {
  return {
    stream: record.stream,
    region: record.region,
    scope: record.scope,
    org_id: record.org_id,
    sku_id: record.sku_id,
    lane: record.lane ?? null,
    keep_ratio: record.keep_ratio,
    keep_slow_ms: record.keep_slow_ms,
    size_threshold_bytes: record.size_threshold_bytes,
    large_full_keep_ratio: record.large_full_keep_ratio,
    note: record.note,
  };
}

export function policiesInGroup(
  rows: readonly SamplingPolicyInput[],
  group: SamplingPolicyGroup,
): readonly SamplingPolicyInput[] {
  return rows
    .filter((row) => row.stream === group.stream && row.region === group.region)
    .sort((a, b) => {
      const byScope = SCOPE_DISPLAY_ORDER.indexOf(a.scope) - SCOPE_DISPLAY_ORDER.indexOf(b.scope);
      if (byScope !== 0) return byScope;
      const byOrg = (a.org_id ?? '').localeCompare(b.org_id ?? '');
      if (byOrg !== 0) return byOrg;
      const byLane = (a.lane ?? '').localeCompare(b.lane ?? '');
      if (byLane !== 0) return byLane;
      return (a.sku_id ?? '').localeCompare(b.sku_id ?? '');
    });
}

export interface SamplingPolicyDraft {
  readonly stream: SamplingStream;
  readonly region: SamplingRegion;
  readonly scope: SamplingScope;
  readonly org_id: string;
  readonly sku_id: string;
  /** Picked SKU namespaces; one row per lane when the scope takes a lane. */
  readonly lanes: readonly string[];
  readonly keep_ratio: string;
  readonly keep_slow_ms: string;
  readonly size_threshold_bytes: string;
  readonly large_full_keep_ratio: string;
  readonly note: string;
}

export type SamplingDraftField = 'org_id' | 'sku_id' | 'lanes' | 'scope' | SamplingValueField;
export type SamplingDraftError =
  | 'org_required'
  | 'sku_required'
  | 'lane_required'
  | 'invalid_lane'
  | 'invalid_ratio'
  | 'invalid_int'
  | 'duplicate';
export type SamplingDraftErrors = Partial<Record<SamplingDraftField, SamplingDraftError>>;

export function emptyDraft(group: SamplingPolicyGroup): SamplingPolicyDraft {
  return {
    stream: group.stream,
    region: group.region,
    scope: 'region',
    org_id: '',
    sku_id: '',
    lanes: [],
    keep_ratio: '',
    keep_slow_ms: '',
    size_threshold_bytes: '',
    large_full_keep_ratio: '',
    note: '',
  };
}

function text(value: number | string | null | undefined): string {
  return value === null || value === undefined ? '' : String(value);
}

export function draftFromInput(row: SamplingPolicyInput): SamplingPolicyDraft {
  return {
    stream: row.stream,
    region: row.region,
    scope: row.scope,
    org_id: text(row.org_id),
    sku_id: text(row.sku_id),
    lanes: row.lane === null || row.lane === undefined ? [] : [row.lane],
    keep_ratio: text(row.keep_ratio),
    keep_slow_ms: text(row.keep_slow_ms),
    size_threshold_bytes: text(row.size_threshold_bytes),
    large_full_keep_ratio: text(row.large_full_keep_ratio),
    note: text(row.note),
  };
}

const DECIMAL_RE = /^(?:\d+(?:\.\d+)?|\.\d+)$/;
const INT_RE = /^\d+$/;

type Parsed = { readonly ok: true; readonly value: number | null } | { readonly ok: false };

export function parseSamplingValue(field: SamplingValueField, raw: string): Parsed {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return { ok: true, value: null };
  if (samplingFieldKind(field) === 'ratio') {
    if (!DECIMAL_RE.test(trimmed)) return { ok: false };
    const value = Number(trimmed);
    return value >= 0 && value <= 1 ? { ok: true, value } : { ok: false };
  }
  if (!INT_RE.test(trimmed)) return { ok: false };
  const value = Number(trimmed);
  return Number.isSafeInteger(value) && value >= 1 ? { ok: true, value } : { ok: false };
}

export type ParsedDraft =
  | { readonly ok: true; readonly rows: readonly SamplingPolicyInput[] }
  | { readonly ok: false; readonly errors: SamplingDraftErrors };

/** "claude-code, codex  qwen" → trimmed, de-duplicated names (free-text lane entry). */
export function splitLanes(raw: string): readonly string[] {
  return [...new Set(raw.split(/[\s,]+/).filter((lane) => lane.length > 0))];
}

/**
 * Turns a dialog draft into rows (one per picked lane for the lane scopes,
 * otherwise one), or the per-field errors that block it. `otherKeys` are the
 * policy keys of every OTHER row in the set (the row being edited excluded),
 * so re-saving a row in place is not a duplicate.
 */
export function parseDraft(draft: SamplingPolicyDraft, otherKeys: ReadonlySet<string>): ParsedDraft {
  const errors: { -readonly [K in SamplingDraftField]?: SamplingDraftError } = {};
  const orgId = draft.org_id.trim();
  const skuId = draft.sku_id.trim();
  const needsOrg = scopeNeedsOrg(draft.scope);
  const needsSku = scopeNeedsSku(draft.scope);
  const needsLane = scopeNeedsLane(draft.scope);
  const lanes = needsLane ? [...new Set(draft.lanes.map((lane) => lane.trim()).filter((lane) => lane.length > 0))] : [];
  if (needsOrg && orgId.length === 0) errors.org_id = 'org_required';
  if (needsSku && skuId.length === 0) errors.sku_id = 'sku_required';
  if (needsLane) {
    if (lanes.length === 0) errors.lanes = 'lane_required';
    else if (lanes.some((lane) => lane.length > SAMPLING_LANE_MAX_LENGTH || !SAMPLING_LANE_PATTERN.test(lane))) {
      errors.lanes = 'invalid_lane';
    }
  }

  const values: { -readonly [K in SamplingValueField]: number | null } = {
    keep_ratio: null,
    keep_slow_ms: null,
    size_threshold_bytes: null,
    large_full_keep_ratio: null,
  };
  for (const field of fieldsForStream(draft.stream)) {
    const parsed = parseSamplingValue(field, draft[field]);
    if (parsed.ok) {
      values[field] = parsed.value;
    } else {
      errors[field] = samplingFieldKind(field) === 'ratio' ? 'invalid_ratio' : 'invalid_int';
    }
  }

  const note = draft.note.trim();
  const rows: SamplingPolicyInput[] = (needsLane ? lanes : [null]).map((lane) => ({
    stream: draft.stream,
    region: draft.region,
    scope: draft.scope,
    org_id: needsOrg ? orgId : null,
    sku_id: needsSku ? skuId : null,
    lane,
    ...values,
    note: note.length === 0 ? null : note,
  }));
  if (Object.keys(errors).length === 0 && rows.some((row) => otherKeys.has(samplingPolicyKey(row)))) {
    errors.scope = 'duplicate';
  }
  return Object.keys(errors).length > 0 ? { ok: false, errors } : { ok: true, rows };
}
